import { Device, Document, TaskState } from "@models";
import { Menu, MenuItem, TextField } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SelectableDataKey } from "src/components/Chat/types";
import { TemplateConfig } from "src/types";

interface MenuContextProps {
  anchorEl: HTMLElement | null;
  isMenuOpen: boolean;
  handleMenuClose: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  uniqueTemplateDocuments: Document[];
  devices?: Pick<Device, "id" | "name">[];
  filteredDataKeys: SelectableDataKey[];
  filteredTasks: TaskState[];
  handleDocumentSelect: (document: Document) => void;
  handleDataKeySelect: (dataKey: SelectableDataKey) => void;
  handleTaskSelect: (task: TaskState) => void;
  templatesConfig: TemplateConfig;
}

export const MenuContext: React.FC<MenuContextProps> = ({
  anchorEl,
  isMenuOpen,
  handleMenuClose,
  searchQuery,
  setSearchQuery,
  uniqueTemplateDocuments,
  devices = [],
  filteredDataKeys,
  filteredTasks,
  handleDocumentSelect,
  handleDataKeySelect,
  handleTaskSelect,
  templatesConfig,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const getDeviceLabel = (deviceId: string | null | undefined) =>
    deviceId
      ? (devices.find((device) => device.id === deviceId)?.name ?? deviceId)
      : "Organization";

  useEffect(() => {
    if (isMenuOpen && inputRef.current) {
      // Use setTimeout to ensure the menu is fully rendered before focusing
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) {
      setActiveIndex(-1);
      itemRefs.current = [];
    }
  }, [isMenuOpen]);

  // Sort documents alphabetically by title
  const sortedDocuments = [...uniqueTemplateDocuments].sort((a, b) => {
    const templateA =
      templatesConfig?.[a.templateKey as keyof typeof templatesConfig];
    const templateB =
      templatesConfig?.[b.templateKey as keyof typeof templatesConfig];
    const titleA = a.title || templateA?.defaultTitle || "Untitled Document";
    const titleB = b.title || templateB?.defaultTitle || "Untitled Document";
    return titleA.localeCompare(titleB);
  });

  // Sort data keys alphabetically by dataKey name
  const sortedDataKeys = [...filteredDataKeys].sort((a, b) => {
    return a.dataKey.localeCompare(b.dataKey);
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const titleA = a.notesDocument?.title || a.taskKey;
    const titleB = b.notesDocument?.title || b.taskKey;
    return titleA.localeCompare(titleB);
  });

  const totalSelectableItems = useMemo(() => {
    return sortedDocuments.length + sortedDataKeys.length + sortedTasks.length;
  }, [sortedDocuments.length, sortedDataKeys.length, sortedTasks.length]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [searchQuery, totalSelectableItems]);

  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }
    const item = itemRefs.current[activeIndex];
    item?.focus();
  }, [activeIndex]);

  const moveFocus = (nextIndex: number) => {
    if (totalSelectableItems === 0) {
      return;
    }
    let target = nextIndex;
    if (target < 0) {
      target = totalSelectableItems - 1;
    }
    if (target >= totalSelectableItems) {
      target = 0;
    }
    setActiveIndex(target);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(0);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(totalSelectableItems - 1);
    }
  };

  const handleItemKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(index + 1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(index - 1);
      return;
    }
  };

  return (
    <Menu
      id="documents-menu"
      anchorEl={anchorEl}
      open={isMenuOpen}
      onClose={handleMenuClose}
      MenuListProps={{
        "aria-labelledby": "documents-button",
      }}
      disablePortal
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        root: {
          sx: {
            // This is so the menu is not on top of the button
            left: 30,
          },
        },
        paper: {
          sx: {
            minWidth: 250,
            maxHeight: 200,
            width: 250,
            height: 200,
            overflow: "hidden",
            paddingTop: 0,
          },
        },
      }}
    >
      <div className="p-3 border-b border-gray-200 ">
        <TextField
          size="small"
          placeholder="Search documents, data keys, and tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleInputKeyDown}
          fullWidth
          variant="outlined"
          inputRef={inputRef}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "0.875rem",
            },

            "& .MuiOutlinedInput-notchedOutline": {
              top: "-10px",
            },
          }}
        />
      </div>
      <div className="relative max-h-[145px] overflow-y-auto pb-2">
        {uniqueTemplateDocuments.length === 0 &&
          filteredDataKeys.length === 0 &&
          filteredTasks.length === 0 && (
            <MenuItem disabled>
              <span className="text-gray-500 text-sm">
                {searchQuery
                  ? "No documents, data keys, or tasks found"
                  : "No documents, data keys, or tasks available"}
              </span>
            </MenuItem>
          )}

        {/* Documents Section */}
        {sortedDocuments.length > 0 && (
          <>
            <MenuItem disabled>
              <span className="text-gray-500 text-sm font-medium">
                Documents
              </span>
            </MenuItem>
            {sortedDocuments.map((doc, index) => {
              const itemIndex = index;
              const template =
                templatesConfig?.[
                  doc.templateKey as keyof typeof templatesConfig
                ];
              const title =
                doc.title || template?.defaultTitle || "Untitled Document";
              const deviceLabel = getDeviceLabel(doc.deviceId);

              return (
                <MenuItem
                  key={doc.id}
                  onClick={() => handleDocumentSelect(doc)}
                  onKeyDown={(event) => handleItemKeyDown(event, itemIndex)}
                  ref={(element) => {
                    itemRefs.current[itemIndex] = element;
                  }}
                  sx={{
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  className="gap-3"
                >
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="text-sm font-medium w-full whitespace-normal break-words [overflow-wrap:anywhere]">
                      {title}
                    </span>
                    <span className="text-xs text-gray-500 w-full whitespace-normal break-words [overflow-wrap:anywhere]">
                      {deviceLabel}
                    </span>
                  </div>
                </MenuItem>
              );
            })}
          </>
        )}

        {/* Data Keys Section */}
        {sortedDataKeys.length > 0 && (
          <>
            <MenuItem disabled>
              <span className="text-gray-500 text-sm font-medium">
                Data Keys
              </span>
            </MenuItem>
            {sortedDataKeys.map((item, index) => {
              const itemIndex = sortedDocuments.length + index;
              const template = item.document
                ? templatesConfig?.[
                    item.document.templateKey as keyof typeof templatesConfig
                  ]
                : null;
              const documentTitle = item.document
                ? item.document.title ||
                  template?.defaultTitle ||
                  "Untitled Document"
                : null;
              const dataKeyTitle = item.dataKey;
              const sourceLabel =
                item.source === "document" && documentTitle
                  ? `from ${documentTitle}`
                  : item.source === "system"
                    ? "Built-in data key"
                    : null;
              const deviceLabel =
                item.source === "document"
                  ? getDeviceLabel(item.document?.deviceId)
                  : null;

              return (
                <MenuItem
                  key={
                    item.document
                      ? `${item.dataKey}-${item.document.id}`
                      : `system-${item.dataKey}`
                  }
                  onClick={() => handleDataKeySelect(item)}
                  onKeyDown={(event) => handleItemKeyDown(event, itemIndex)}
                  ref={(element) => {
                    itemRefs.current[itemIndex] = element;
                  }}
                  sx={{
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  className="gap-3"
                >
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="text-sm font-medium w-full whitespace-normal break-words [overflow-wrap:anywhere]">
                      {dataKeyTitle}
                    </span>
                    {sourceLabel && (
                      <span className="text-xs text-gray-500 w-full whitespace-normal break-words [overflow-wrap:anywhere]">
                        {sourceLabel}
                      </span>
                    )}
                    {deviceLabel && (
                      <span className="text-xs text-gray-500 w-full whitespace-normal break-words [overflow-wrap:anywhere]">
                        {deviceLabel}
                      </span>
                    )}
                  </div>
                </MenuItem>
              );
            })}
          </>
        )}

        {/* Tasks Section */}
        {sortedTasks.length > 0 && (
          <>
            <MenuItem disabled>
              <span className="text-gray-500 text-sm font-medium">Tasks</span>
            </MenuItem>
            {sortedTasks.map((task, index) => {
              const itemIndex =
                sortedDocuments.length + sortedDataKeys.length + index;
              const taskTitle = task.notesDocument?.title || task.taskKey;
              const shortTaskId = task.id
                .replace(/-/g, "")
                .slice(0, 5)
                .toUpperCase();
              const taskLabel = `#${shortTaskId} ${taskTitle}`;

              return (
                <MenuItem
                  key={task.id}
                  onClick={() => handleTaskSelect(task)}
                  onKeyDown={(event) => handleItemKeyDown(event, itemIndex)}
                  ref={(element) => {
                    itemRefs.current[itemIndex] = element;
                  }}
                  sx={{
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  className="gap-3"
                >
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="text-sm font-medium w-full whitespace-normal break-words [overflow-wrap:anywhere]">
                      {taskLabel}
                    </span>
                  </div>
                </MenuItem>
              );
            })}
          </>
        )}
      </div>
    </Menu>
  );
};
